import { EnterpriseProfileDataContext } from '@/components/elements/profile/enterprise/client/context/EnterpriseProfileDataContext';
import {
  Button,
  Dialog,
  Drawer,
  IconButton,
  TextField,
  styled,
  useMediaQuery,
} from '@mui/material';
import Image from 'next/image';
import { useContext } from 'react';
import useUpdateBusinessInfo from '../../../hooks/profile/business/useUpdateBusinessInfo';
import SnackBarProvider from '../../../state/snackbar-provider/SnackBarProvider';
import { ConfirmModalVerse2 } from './ConfirmModal';

const CustomTextField = styled(TextField)(
  () =>
    `
    overflow: hidden;

    .Mui-focused {
      color: #707070;
    }

    .MuiInputLabel-root {
      color: #707070 !important;
    }

    .MuiInputBase-root {
      &::before {
        display: none;
      }
      
      &::after {
        display: none;
      }

      & .MuiInputBase-input {
        color: #242424;
        font-weight: 500;
        font-size: 16px;
        line-height: 140%;
      }
    }
  `
);

interface IEditBusinessInfoModal {
  onClose: () => void;
  open: boolean;
  defaultData: {
    name: string;
    description: string;
  };
}

const EditBusinessInfoModalMain = ({
  onClose: _onClose,
  open,
  defaultData,
}: IEditBusinessInfoModal) => {
  const isMobile = useMediaQuery('(max-width:425px)');
  const { setData } = useContext(EnterpriseProfileDataContext);

  const {
    name,
    setName,
    description,
    setDescription,
    handleCloseModal,
    handleSubmitInfo,
    openConfirmClose,
    setOpenConfirmClose,
  } = useUpdateBusinessInfo(defaultData, open);

  const handleOnSave = () => {
    handleSubmitInfo(() => {
      _onClose();
      setData((prev) => {
        const newState = { ...prev };
        if (!newState.profiles) return prev;

        newState.profiles.bio = description;
        newState.profiles.qbankData.fullName = name;

        return newState as any;
      });
    });
  };

  const onClose = () => {
    handleCloseModal(_onClose);
  };

  const main = (
    <div className="flex h-full flex-col gap-y-4 px-6 py-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">Chỉnh sửa thông tin</h1>
        <IconButton onClick={onClose}>
          <Image
            alt="close modal"
            src="/icons/close-icon.svg"
            width={24}
            height={24}
          />
        </IconButton>
      </div>

      <div className="mb-4 flex flex-col gap-4">
        <h2 className="text-xl font-bold leading-[150%]">
          Thông tin doanh nghiệp
        </h2>

        <CustomTextField
          required
          variant="filled"
          label="Tên doanh nghiệp"
          value={name}
          className="!rounded-lg !bg-Action/Resting"
          onChange={(e) => setName(e.target.value)}
        />
        <CustomTextField
          label="Mô tả doanh nghiệp"
          variant="filled"
          value={description}
          className="!rounded-lg !bg-Action/Resting"
          onChange={(e) => setDescription(e.target.value)}
          multiline
          inputProps={{
            maxLength: 99,
          }}
        />
      </div>

      {!isMobile ? (
        <div
          className={`flex w-full items-center justify-end justify-items-end gap-x-2`}
        >
          <Button
            style={{ textTransform: 'none' }}
            className={`!bg-white !text-Primary/Main hover:!bg-white`}
            onClick={onClose}
          >
            {'Huỷ'}
          </Button>
          <Button
            disabled={!name}
            style={{ textTransform: 'none' }}
            className={`!bg-primary_lighter !text-white hover:!bg-primary_lighter disabled:!bg-Action/Disabled_background disabled:!text-Text/Lightbackground/Disabled`}
            onClick={handleOnSave}
          >
            {'Lưu thay đổi'}
          </Button>
        </div>
      ) : (
        <div className="mt-auto flex flex-col gap-2">
          <Button
            disabled={!name}
            style={{ textTransform: 'none' }}
            className={`!w-full !bg-primary_lighter !p-4 !text-base !font-semibold !leading-[100%] !text-white hover:!bg-primary_lighter disabled:!bg-Action/Disabled_background disabled:!text-Text/Lightbackground/Disabled`}
            onClick={handleOnSave}
          >
            {'Lưu thay đổi'}
          </Button>
          <Button
            style={{ textTransform: 'none' }}
            className={`!w-full !border !border-solid !border-Primary/Border !bg-[#B1E7FF4D] !p-4 !text-base !font-semibold !leading-[100%] !text-Primary/Main hover:!bg-white`}
            onClick={onClose}
          >
            {'Huỷ'}
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <>
      {!isMobile ? (
        <Dialog
          fullWidth
          maxWidth={'md'}
          onClose={(_e, reason) =>
            reason !== 'backdropClick' ? onClose : undefined
          }
          open={open}
        >
          {main}
        </Dialog>
      ) : (
        <Drawer anchor={'bottom'} open={open} onClose={onClose}>
          <div className="h-[90dvh]">{main}</div>
        </Drawer>
      )}

      <ConfirmModalVerse2
        open={openConfirmClose}
        onClose={() => setOpenConfirmClose(false)}
        cancelCallback={() => setOpenConfirmClose(false)}
        confirmCallback={_onClose}
        title={'Thay đổi chưa lưu'}
        description={
          'Nếu thoát, những thông tin đang thay đổi sẽ không được lưu. Ban chắc chắn muốn thoát?'
        }
        cancelContent={'Huỷ'}
        confirmContent={'Thoát'}
        buttonFloatRight
      />
    </>
  );
};

const EditBusinessInfoModal = (props: IEditBusinessInfoModal) => (
  <SnackBarProvider>
    <EditBusinessInfoModalMain {...props} />
  </SnackBarProvider>
);

export default EditBusinessInfoModal;
